import { SkeletonRect, SkeletonWrapper } from "components/Skeleton/Skeleton";
import { generateIndexName } from "lib/algolia";
import {
  getSubDomain,
  isBlueBirdURL,
  isSubdomainBluebird,
  useIsSubdomain,
  useIsTenant,
} from "lib/host";
import { ArrayObjectSortBy, parseDataParams, ShuffleArray } from "lib/object";
import useInfiniteScroll from "lib/useInfiniteScroll";
import { useAuthStore } from "modules/Auth/authStore";
import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";
import Section from "./Section";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchStore } from "components/InstantSearchBox/searchStore";
import IconClose from "components/Icon/IconClose";
import { isBrowser } from "lib/ssr";
import { useAccessCourseTenant } from "modules/Explore/exploreApi";
import dynamic from "next/dynamic";
import { MoonLoader } from "react-spinners";
import { useIsDesktop } from "lib/device";
import { useGetCoursesByTenantSlug } from "./homeTenantApi";
import { getCookie } from "lib/helper";
import { useHomeStore } from "modules/Home/homeStore";
import { trackEventGA, trackEventGTM } from "lib/amplitude";

const CardCourseWithLesson = dynamic(
  () =>
    import("components/Card/CardCourseWithLesson").then((mod) => mod.default),
  { ssr: false }
);

const HomeCard: React.FC<{
  data?: any;
  filters?: any;
  sortDesc?: any;
  searchText?: string;
  textValue?: string;
  sections?: any;
  isSquare?: boolean;
  isHidden?: any;
  keywordOthers?: any[];
  isKeywordsSearched?: string;
  handleReset?: any;
}> = ({
  data,
  filters,
  sortDesc,
  searchText,
  textValue,
  sections,
  isSquare = false,
  isHidden,
  keywordOthers,
  isKeywordsSearched,
  handleReset,
}) => {
  const isDesktop = useIsDesktop();
  const screen = typeof window !== "undefined" && window.innerWidth;
  const isMobile = screen < 770;
  const { t } = useTranslation("common");
  const { t: tCourse } = useTranslation("course");
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [nbHits, setNbHits] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [indexName, setIndexName] = useState("");
  const domain = typeof window !== "undefined" && window.location.hostname;
  const { currentRecommend, totalFilter, setTotalFilter } = useSearchStore(
    (state) => ({
      currentRecommend: state.currentRecommend,
      totalFilter: state.totalFilter,
      setTotalFilter: state.setTotalFilter,
    })
  );
  const setRecommendData = useSearchStore((state) => state.setRecommendData);

  const router = useRouter();
  const q = router?.query?.q ?? "";
  const isB2B = useIsTenant() || useIsSubdomain();
  const institutionNames =
    typeof router.query.institutionKey == "string" &&
    router.query.institutionKey
      ? router.query.institutionKey.split(",")
      : [];
  const institutionKeyFilter = institutionNames.length
    ? `${institutionNames.map((ins) => `institution:"${ins}"`).join(" OR ")}`
    : "";

  const tagsNames =
    typeof router.query.tagsKey == "string" && router.query.tagsKey
      ? router.query.tagsKey.split(",")
      : [];
  const tagsKeyFilter = tagsNames.length
    ? `${tagsNames.map((ins) => `tags:"${ins}"`).join(" OR ")}`
    : "";

  const sortByFilter =
    typeof router.query.sortBy == "string" ? router.query.sortBy : "";

  const referralCode =
    getCookie("ref_code_gk") && getCookie("ref_code_gk") !== "null"
      ? getCookie("ref_code_gk")
      : typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("referral")
      : "";

  const getCoursesByTenant = useGetCoursesByTenantSlug();

  const [tempData, setTempData] = useState(null);
  const subDomain = isBrowser() ? getSubDomain(window.location.hostname) : null;

  const PER_PAGE = 9;
  const [hasMore, setHasMore] = useState(true);
  const { loginState, currentUser } = useAuthStore((state) => ({
    loginState: state.loginState,
    currentUser: state.currentUser,
  }));
  const [emptyResults, setEmpty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { setDataCourseId } = useHomeStore();

  // State for course data - initialized as null
  const [mergedData, setMergedData] = useState<any[] | null>(null);

  const observerRef = useRef<HTMLDivElement | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Intersection Observer implementation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          hasMore &&
          !isLoading &&
          mergedData !== null
        ) {
          setPage((prev) => prev + 1);
        }
      },
      {
        root: null,
        rootMargin: "100px",
        threshold: 0.8,
      }
    );

    const currentRef = observerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMore, isLoading, mergedData]);

  // Reset when filters change
  useEffect(() => {
    setMergedData([]);
    setPage(1);
    setHasMore(true);
    setIsInitialLoad(true);
  }, [
    q,
    institutionKeyFilter,
    tagsKeyFilter,
    sortByFilter,
    isKeywordsSearched,
  ]);

  // forbidden courses
  const { forbiddenCourses } = useHomeStore();

  // Main data fetching function
  const getListCourses = (idxName: string) => {
    const allFilter = [institutionKeyFilter, tagsKeyFilter]
      .filter((a) => a)
      .join(" AND ");

    const params = {
      q,
      facetFilters: allFilter,
      page: page - 1,
      hitsPerPage: PER_PAGE,
    };

    if (isAnyFiltered || isKeywordsSearched) {
      setIsLoading(true);

      fetch(
        `/api/search-algolia?indexName=${idxName}&${new URLSearchParams(
          parseDataParams(params)
        ).toString()}`
      )
        .then((res) => res.json())
        .then((res) => {
          if (res?.data?.hits?.length) {
            const algoliaHits = res.data.hits;
            const nbHits = res.data.nbHits;
            const totalPages = Math.ceil(nbHits / PER_PAGE);

            setHasMore(page < totalPages);
            setTotalData(nbHits);

            // Process initial data (sorting, shuffling)
            let processed = sortByFilter
              ? ArrayObjectSortBy(algoliaHits, sortByFilter, true)
              : q
              ? algoliaHits
              : ShuffleArray(algoliaHits);

            // Filter for anonymous users
            if (loginState !== "LOGIN") {
              processed = processed.filter((a) => !a.tenant);
              updateMergedData(processed);
              return;
            }

            // If logged have not referral
            if (!currentUser?.referral) {
              updateMergedData(processed);
              return;
            }

            // If logged have referral
            if (currentUser?.referral) {
              const filtered = processed.filter(
                (a) => !forbiddenCourses.includes(a.slug)
              );
              updateMergedData(filtered);
              return;
            }
          } else {
            setHasMore(false);
            setEmpty(true);
            setIsLoading(false);
          }
        })
        .catch((err) => {
          console.error("Fetch error:", err);
          setEmpty(true);
          setIsLoading(false);
        });
    }
  };

  // Helper function to update merged data
  const updateMergedData = (newData: any[]) => {
    const topThreeCourseIds = newData.slice(0, 3).map((course) => course.id);

    trackEventGTM({
      event: "explore_searchbar_a",
      property: {
        query: q,
        course_id: topThreeCourseIds,
        page: router.pathname,
      },
    });
    trackEventGA({
      event: "explore_searchbar_a",
      property: {
        query: q,
        course_id: topThreeCourseIds,
        page: router.pathname,
      },
    });

    setMergedData((prev) => {
      if (isInitialLoad) {
        setIsInitialLoad(false);
        return newData;
      }

      // Filter out duplicates
      const uniqueNewData = newData.filter(
        (newItem) =>
          !prev.some((existingItem) => existingItem.id === newItem.id)
      );

      return [...prev, ...uniqueNewData];
    });

    setNbHits(newData.length);
    setEmpty(newData.length === 0);
    setIsLoading(false);
  };

  useEffect(() => {
    const computeIndexName = () => {
      if (!isKeywordsSearched && currentUser) {
        if (currentUser.referral.includes("gaji-c")) {
          return currentUser.referral !== "gaji-c0000117"
            ? generateIndexName("gaji")
            : generateIndexName(currentUser.referral);
        }
        return generateIndexName(
          window.location.hostname === "e-learning.bluebirdgroup.com"
            ? "bluebird"
            : window.location.hostname === "lms.injourney.id"
            ? "injourney"
            : currentUser.referral === "bssn"
            ? "tapinkab"
            : currentUser.referral
        );
      }
      return generateIndexName(
        window.location.hostname === "e-learning.bluebirdgroup.com"
          ? "bluebird"
          : window.location.hostname === "lms.injourney.id"
          ? "injourney"
          : subDomain === "bssn"
          ? "tapinkab"
          : subDomain
      );
    };

    const indexName = computeIndexName();
    if (!["moodle", "example"].includes(indexName)) {
      getListCourses(indexName);
    }
  }, [
    page,
    q,
    institutionKeyFilter,
    tagsKeyFilter,
    sortByFilter,
    isKeywordsSearched,
  ]);

  useEffect(() => {
    const filter = sortByFilter != "" ? 1 : 0;
    const totalNewFilter = tagsNames.length + institutionNames.length + filter;
    if (totalFilter != totalNewFilter) {
      setTotalFilter(totalNewFilter);
    }
  }, [tagsNames, institutionNames]);

  const isAnyFiltered =
    tagsKeyFilter !== "" ||
    institutionKeyFilter !== "" ||
    sortByFilter !== "" ||
    q !== "";

  const shouldShowCourseList =
    isAnyFiltered && mergedData !== null && mergedData.length > 0;
  const shouldShowEmptyState =
    isAnyFiltered && (mergedData === null || mergedData.length === 0);

  return (
    <>
      {!shouldShowEmptyState &&
        (isAnyFiltered || isKeywordsSearched) &&
        !isLoading &&
        nbHits && (
          <div className="pb-24 flex justify-start items-center px-16 md:px-0 gap-8">
            <div
              className="font-light items-center flex"
              dangerouslySetInnerHTML={{
                __html: t(
                  tagsKeyFilter !== "" || institutionKeyFilter !== ""
                    ? "descriptionResultFilter"
                    : "descriptionResultSearch",
                  {
                    count: totalData,
                    text: `${
                      tagsKeyFilter !== "" || institutionKeyFilter !== ""
                        ? ""
                        : q ||
                          isKeywordsSearched ||
                          (sortByFilter === "rating_avg"
                            ? "Rating Tertinggi"
                            : sortByFilter === "created_date"
                            ? "Terbaru"
                            : "Populer")
                    }`,
                    additionalS: mergedData && mergedData.length > 1 ? "s" : "",
                  }
                ),
              }}
            ></div>
            <div
              onClick={() => {
                q
                  ? router.push({
                      pathname: "/",
                    })
                  : handleReset();
              }}
              className="cursor-pointer block"
            >
              <IconClose size={20} color="#883CFF" />
            </div>
          </div>
        )}

      {isLoading && isAnyFiltered && (
        <div className={"grid grid-cols-1 mb-24 gap-40"}>
          {[...Array(9)].map((_, index) => (
            <SkeletonWrapper key={index} className="flex items-start gap-8">
              <SkeletonRect className="h-[239px] flex-shrink-0 w-[426px] rounded-8"></SkeletonRect>
              <SkeletonWrapper className="flex w-full flex-col gap-8">
                <SkeletonRect className="h-[24px] rounded-8"></SkeletonRect>
                <SkeletonRect className="h-[36px] rounded-8"></SkeletonRect>
                <SkeletonRect className="h-[24px] rounded-8"></SkeletonRect>
                <SkeletonRect className="h-[56px] rounded-8"></SkeletonRect>
              </SkeletonWrapper>
            </SkeletonWrapper>
          ))}

          <div className="mt-10 flex justify-center">
            <MoonLoader size={isDesktop ? 45 : 20} />
          </div>
        </div>
      )}

      {shouldShowCourseList && (
        <div className={"grid grid-cols-1 mb-24 gap-40"}>
          {mergedData.map((course, index) => {
            const isLast = index === mergedData.length - 1;

            return (
              <div ref={isLast ? observerRef : null} key={course.id}>
                <CardCourseWithLesson
                  {...course}
                  isRow
                  isSquare={isSquare}
                  tracker={{
                    event: "b2b_homepage_course_a",
                    property: {
                      course_name: course?.name,
                      course_slug: course?.slug,
                      institution_name: course?.institution?.name,
                      section: 3,
                      category_name: `Default`,
                      index,
                      is_mandatory_course: false,
                    },
                  }}
                />
              </div>
            );
          })}

          {isLoading && hasMore && (
            <>
              {[...Array(1)].map((_, index) => (
                <SkeletonWrapper key={index} className="flex items-start gap-8">
                  <SkeletonRect className="h-[239px] flex-shrink-0 w-[426px] rounded-8"></SkeletonRect>
                  <SkeletonWrapper className="flex w-full flex-col gap-8">
                    <SkeletonRect className="h-[24px] rounded-8"></SkeletonRect>
                    <SkeletonRect className="h-[36px] rounded-8"></SkeletonRect>
                    <SkeletonRect className="h-[24px] rounded-8"></SkeletonRect>
                    <SkeletonRect className="h-[56px] rounded-8"></SkeletonRect>
                  </SkeletonWrapper>
                </SkeletonWrapper>
              ))}

              <div className="mt-10 flex justify-center">
                <MoonLoader size={isDesktop ? 45 : 20} />
              </div>
            </>
          )}
        </div>
      )}

      {shouldShowEmptyState && (
        <div>
          <div className="pb-24 flex justify-start items-center px-16 md:px-0 gap-8 mt-16">
            <div
              className="font-light items-center flex"
              dangerouslySetInnerHTML={{
                __html: t("descriptionResult"),
              }}
            ></div>
            <div onClick={handleReset} className="cursor-pointer block">
              <IconClose size={20} color="#883CFF" />
            </div>
          </div>

          <div className="px-16 md:px-0">
            <Section
              tag={null}
              data={currentRecommend}
              title={
                isAnyFiltered || isKeywordsSearched
                  ? tCourse("Rekomendasi Kursus Lainnya")
                  : ""
              }
              section={4}
              emoji="✨"
              id={null}
              isHidden={isHidden}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default HomeCard;
